import React, { useState } from "react";
import { useLocalization } from "@fluent/react";
import { Select } from "@narmi/design_system";
import { useNotificationContext } from "cerulean";
import Account from "byzantine/src/Account";
import Role from "byzantine/src/Role";
import { useRolesContext } from "byzantine/src/Role/RolesProvider";
import {
  NON_CUSTOM_ROLE_NAMES,
  buildInitialFormData,
  getDefaultLimits,
} from "./RoleFormUtils";

interface RoleStartFromFieldProps {
  onChange: (update: Record<string, any>) => void;
  accounts: Account[];
  externalAccounts: Account[];
  features?: Record<string, any>;
  /** Called with true while a picked role's details load, so the form can hold off saving. */
  onSeedingChange?: (isSeeding: boolean) => void;
}

/**
 * "Start from" select on the new role page: copies another custom role's permissions
 * and limits into the form, keeping the name and description typed so far.
 */
export default function RoleStartFromField({
  onChange,
  accounts,
  externalAccounts,
  features,
  onSeedingChange,
}: RoleStartFromFieldProps) {
  const { l10n } = useLocalization();
  const { roles, fetchRoleDetail } = useRolesContext();
  const { sendNotification } = useNotificationContext();
  const [startFromUuid, setStartFromUuid] = useState<string>("");
  const [isSeedingRoleData, setIsSeedingRoleData] = useState(false);

  const startFromRoles = roles.filter(
    (role: Role) =>
      !NON_CUSTOM_ROLE_NAMES.has(role.name) &&
      !role.isDeleted() &&
      !role.isUnassigned()
  );

  // The roles list is lean, so the picked role's per-account permissions come from its detail.
  const onStartFromChange = async (roleUuid: string) => {
    setStartFromUuid(roleUuid);
    setIsSeedingRoleData(true);
    onSeedingChange?.(true);
    try {
      const sourceRole = await fetchRoleDetail(roleUuid);
      const seeded: Record<string, any> = buildInitialFormData({
        role: sourceRole,
        accounts,
        externalAccounts,
        defaultLimits: getDefaultLimits(features),
        features,
        isDuplicate: true,
      });
      delete seeded.role_name;
      delete seeded.role_description;
      onChange(seeded);
    } catch {
      sendNotification({
        type: "negative",
        text: l10n.getString("org-role-start-from-error"),
      });
    } finally {
      setIsSeedingRoleData(false);
      onSeedingChange?.(false);
    }
  };

  return (
    <div className="nds-span-6 nds-span-tablet-4 nds-span-mobile-2 nds-input-style-override">
      <label
        style={{
          display: "block",
          marginBottom: "8px",
          fontWeight: "500",
        }}
      >
        {l10n.getString("org-role-start-from-label")}
      </label>
      <Select
        value={startFromUuid}
        onChange={onStartFromChange}
        disabled={startFromRoles.length === 0 || isSeedingRoleData}
        label={l10n.getString("org-role-start-from-placeholder")}
      >
        {startFromRoles.map((role: Role) => (
          <Select.Item key={role.uuid} value={role.uuid || ""}>
            {role.getDisplayName()}
          </Select.Item>
        ))}
      </Select>
    </div>
  );
}
